import { useState, useEffect } from 'react';
import { clientApi } from '../../api/clientApi';
import type { ClientResponse } from '../../api/clientApi';
import { useSalonId } from '../../hooks/useSalonId';
import { useSubscription } from '../../hooks/useSubscription';
import { useToast } from '../../contexts/ToastContext';
import { useUpsellModal } from '../../components/UpsellModal';
import { TableSkeleton } from '../../components/ui/Skeleton';
import { EmptyState } from '../../components/ui/EmptyState';
import { Input } from '../../components/ui/Input';
import { Users, Phone, Mail, User } from 'lucide-react';

const ClientListPage = () => {
    const salonId = useSalonId();
    const { getLimit } = useSubscription();
    const { showUpsell } = useUpsellModal();
    const toast = useToast();
    const [clients, setClients] = useState<ClientResponse[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingClient, setEditingClient] = useState<ClientResponse | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    // Pagination state
    const [page, setPage] = useState(0);
    const [pageSize, _setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    const [formData, setFormData] = useState({
        fullName: '',
        phone: '',
        email: '',
        notes: '',
        documentType: 'CC',
        documentNumber: '',
        address: '',
        city: '',
        cityCode: ''
    });

    // Load clients on mount or page change
    useEffect(() => {
        if (!salonId) return;
        loadClients();
    }, [salonId, page, pageSize, searchQuery]);

    const loadClients = async () => {
        if (!salonId) return;
        setIsLoading(true);
        try {
            if (searchQuery.trim()) {
                // Search mode (server-side search, currently returns list)
                const allClients = await clientApi.getAllSalonClients(salonId);

                // Defensive check: ensure data is an array
                if (!Array.isArray(allClients)) {
                    console.warn('Clients data is not an array:', allClients);
                    setClients([]);
                    setTotalElements(0);
                    setTotalPages(0);
                    return;
                }

                const query = searchQuery.toLowerCase();
                const filtered = allClients.filter(client =>
                    client.fullName.toLowerCase().includes(query) ||
                    client.phone.includes(query) ||
                    client.email?.toLowerCase().includes(query)
                );
                setClients(filtered);
                setTotalElements(filtered.length);
                setTotalPages(1); // Disable pagination for search results for now
            } else {
                // Pagination mode
                const pageData = await clientApi.getSalonClientsPaged(salonId, page, pageSize);

                // Defensive check: ensure pageData has expected structure
                if (!pageData || !Array.isArray(pageData.content)) {
                    console.warn('Paged clients data is invalid:', pageData);
                    setClients([]);
                    setTotalPages(0);
                    setTotalElements(0);
                    return;
                }

                setClients(pageData.content);
                setTotalPages(pageData.totalPages ?? 0);
                setTotalElements(pageData.totalElements ?? 0);
            }
        } catch (error) {
            console.error('Error loading clients:', error);
            setClients([]);
            setTotalPages(0);
            setTotalElements(0);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateClick = () => {
        // Subscription Limit Check
        const clientLimit = getLimit('clients');

        if (totalElements >= clientLimit) {
            showUpsell('CLIENT_LIMIT');
            return;
        }

        setEditingClient(null);
        setFormData({ 
            fullName: '', 
            phone: '', 
            email: '', 
            notes: '',
            documentType: 'CC',
            documentNumber: '',
            address: '',
            city: '',
            cityCode: ''
        });
        setShowModal(true);
    };

    const handleEditClick = (client: ClientResponse) => {
        setEditingClient(client);
        setFormData({
            fullName: client.fullName,
            phone: client.phone,
            email: client.email || '',
            notes: client.notes || '',
            documentType: client.documentType || 'CC',
            documentNumber: client.documentNumber || '',
            address: client.address || '',
            city: client.city || '',
            cityCode: client.cityCode || ''
        });
        setShowModal(true);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitError(null);

        // --- VALIDATION START ---
        if (!formData.fullName.trim()) {
            setSubmitError('El nombre es requerido');
            setIsSubmitting(false);
            return;
        }
        if (!formData.phone.trim()) {
            setSubmitError('El teléfono es requerido');
            setIsSubmitting(false);
            return;
        }
        // --- VALIDATION END ---

        try {
            if (editingClient) {
                console.warn("Update API not implemented yet");
                // Ideally this would be await clientApi.updateClient(...)
            } else {
                await clientApi.createClient({
                    salonId: salonId!,
                    fullName: formData.fullName,
                    phone: formData.phone,
                    email: formData.email || undefined,
                    notes: formData.notes || undefined,
                    documentType: formData.documentType,
                    documentNumber: formData.documentNumber || undefined,
                    address: formData.address || undefined,
                    city: formData.city || undefined,
                    cityCode: formData.cityCode || undefined
                });
            }

            setShowModal(false);
            toast.success(editingClient ? 'Cliente actualizado exitosamente' : 'Cliente creado exitosamente');
            setFormData({ 
                fullName: '', 
                phone: '', 
                email: '', 
                notes: '',
                documentType: 'CC',
                documentNumber: '',
                address: '',
                city: '',
                cityCode: ''
            });
            loadClients();
        } catch (error: any) {
            console.error('Error saving client:', error);
            setSubmitError(error.response?.data?.message || 'Error al guardar cliente');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="p-6">
            {/* Header */}
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900">Clientes</h1>
                <p className="text-gray-600 mt-1">Directorio de clientes (CRM)</p>
            </div>

            {/* Toolbar */}
            <div className="mb-6 flex items-center justify-between gap-4">
                {/* Search */}
                <div className="flex-1 max-w-md">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Buscar por nombre, teléfono o email..."
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                            value={searchQuery}
                            onChange={e => {
                                setSearchQuery(e.target.value);
                                setPage(0); // Reset to first page on search
                            }}
                        />
                        <svg
                            className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </div>
                </div>

                {/* Create Button + Usage Badge */}
                <div className="flex items-center gap-3">
                    {/* Usage Badge */}
                    {(() => {
                        const limit = getLimit('clients');
                        const isAtLimit = totalElements >= limit;
                        const isNearLimit = totalElements >= limit - 10 && !isAtLimit;
                        const isUnlimited = limit > 900000;

                        if (isUnlimited) return null;

                        return (
                            <span
                                className={`text-sm px-3 py-1 rounded-full font-medium ${isAtLimit
                                    ? 'bg-red-100 text-red-700'
                                    : isNearLimit
                                        ? 'bg-amber-100 text-amber-700'
                                        : 'bg-gray-100 text-gray-600'
                                    }`}
                                title={`${totalElements} de ${limit} clientes`}
                            >
                                📇 {totalElements}/{limit}
                            </span>
                        );
                    })()}

                    <button
                        onClick={handleCreateClick}
                        className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Nuevo Cliente
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden flex flex-col">
                <div className="overflow-x-auto">
                    {isLoading ? (
                        <TableSkeleton rows={5} columns={5} />
                    ) : clients.length === 0 ? (
                        <EmptyState
                            icon={Users}
                            title={searchQuery ? 'No se encontraron clientes' : 'No hay clientes registrados'}
                            description={searchQuery ? 'Intenta con otro nombre, teléfono o correo electrónico.' : 'Comienza agregando tu primer cliente. Los clientes son esenciales para agendar citas y hacer seguimiento de visitas.'}
                            action={!searchQuery ? {
                                label: 'Agregar Primer Cliente',
                                onClick: handleCreateClick
                            } : undefined}
                            secondaryAction={searchQuery ? {
                                label: 'Limpiar Búsqueda',
                                onClick: () => setSearchQuery('')
                            } : undefined}
                        />
                    ) : (
                        <>
                            {/* Mobile View - Cards */}
                            <div className="md:hidden divide-y divide-gray-200">
                                {clients.map(client => (
                                    <div key={client.id} className="p-4 space-y-2">
                                        <div className="flex items-center justify-between">
                                            <div className="font-medium text-gray-900">
                                                {client.fullName}
                                            </div>
                                            <button
                                                onClick={() => handleEditClick(client)}
                                                className="text-emerald-600 hover:text-emerald-900 bg-emerald-50 px-3 py-1 rounded-md text-sm font-medium"
                                            >
                                                Editar
                                            </button>
                                        </div>
                                        <div className="text-sm text-gray-500 space-y-1">
                                            <div className="flex items-center gap-2">
                                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                                </svg>
                                                {client.phone}
                                            </div>
                                            {client.email && (
                                                <div className="flex items-center gap-2 truncate">
                                                    <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                    </svg>
                                                    <span className="truncate">{client.email}</span>
                                                </div>
                                            )}
                                            {client.lastVisit && (
                                                <div className="flex items-center gap-2 text-xs text-gray-400">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                    </svg>
                                                    Última visita: {new Date(client.lastVisit).toLocaleDateString('es-ES')}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Desktop View - Table */}
                            <table className="min-w-full divide-y divide-gray-200 hidden md:table">
                                <thead className="table-header-premium">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Nombre
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Teléfono
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Email
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Última Visita
                                        </th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Acciones
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {clients.map(client => (
                                        <tr key={client.id} className="table-row-hover">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="font-medium text-gray-900">
                                                    {client.fullName}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {client.phone}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {client.email || '-'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {client.lastVisit ? new Date(client.lastVisit).toLocaleDateString('es-ES') : '-'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <button
                                                    onClick={() => handleEditClick(client)}
                                                    className="text-emerald-600 hover:text-emerald-900 bg-emerald-50 px-3 py-1 rounded-md"
                                                >
                                                    Editar
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </>
                    )}
                </div>

                {/* Pagination Controls - Hide if searching or if no pages */}
                {!searchQuery && totalPages > 1 && (
                    <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                            <div>
                                <p className="text-sm text-gray-700">
                                    Mostrando página <span className="font-medium">{page + 1}</span> de <span className="font-medium">{totalPages}</span> ({totalElements} resultados)
                                </p>
                            </div>
                            <div>
                                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                    <button
                                        onClick={() => setPage(Math.max(0, page - 1))}
                                        disabled={page === 0}
                                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <span className="sr-only">Anterior</span>
                                        <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                            <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                    </button>
                                    <button
                                        onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                                        disabled={page >= totalPages - 1}
                                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <span className="sr-only">Siguiente</span>
                                        <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                        </svg>
                                    </button>
                                </nav>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Modal */}
            {showModal && (
                <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
                    <div className="modal-content max-w-md p-6 space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="text-xl font-bold text-gray-900">{editingClient ? 'Editar Cliente' : 'Nuevo Cliente'}</h3>
                            <button
                                onClick={() => setShowModal(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                ✕
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <Input
                                label="Nombre Completo"
                                type="text"
                                required
                                value={formData.fullName}
                                onChange={e => setFormData({ ...formData, fullName: e.target.value })}
                                disabled={isSubmitting}
                                leftIcon={<User className="w-4 h-4" />}
                                validationState={formData.fullName.length >= 3 ? 'success' : (formData.fullName.length > 0 ? 'error' : 'idle')}
                                showValidation={formData.fullName.length > 0}
                                hint="Mínimo 3 caracteres"
                            />

                            <Input
                                label="Teléfono"
                                type="tel"
                                required
                                value={formData.phone}
                                onChange={e => setFormData({ ...formData, phone: e.target.value })}
                                disabled={isSubmitting}
                                leftIcon={<Phone className="w-4 h-4" />}
                                validationState={/^\d{10}$/.test(formData.phone.replace(/\D/g, '')) ? 'success' : (formData.phone.length > 0 ? 'error' : 'idle')}
                                showValidation={formData.phone.length > 0}
                                hint="Formato: 10 dígitos"
                            />

                            <Input
                                label="Email"
                                type="email"
                                value={formData.email}
                                onChange={e => setFormData({ ...formData, email: e.target.value })}
                                disabled={isSubmitting}
                                leftIcon={<Mail className="w-4 h-4" />}
                                placeholder="opcional"
                                validationState={formData.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) ? 'success' : (formData.email.length > 0 ? 'error' : 'idle')}
                                showValidation={formData.email.length > 0}
                            />

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Notas / Preferencias</label>
                                <textarea
                                    rows={2}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                    value={formData.notes}
                                    onChange={e => setFormData({ ...formData, notes: e.target.value })}
                                    disabled={isSubmitting}
                                    placeholder="Preferencias de color, alergias, etc."
                                />
                            </div>

                            <div className="pt-2 border-t border-gray-100">
                                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Información Fiscal (DIAN)</h4>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 mb-1">Tipo Doc.</label>
                                        <select
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                            value={formData.documentType}
                                            onChange={e => setFormData({ ...formData, documentType: e.target.value })}
                                            disabled={isSubmitting}
                                        >
                                            <option value="CC">Cédula (CC)</option>
                                            <option value="NIT">NIT</option>
                                            <option value="CE">Extranjería (CE)</option>
                                            <option value="PP">Pasaporte</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 mb-1">Número Doc.</label>
                                        <input
                                            type="text"
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                            value={formData.documentNumber}
                                            onChange={e => setFormData({ ...formData, documentNumber: e.target.value })}
                                            disabled={isSubmitting}
                                            placeholder="12345678"
                                        />
                                    </div>
                                </div>
                                <div className="mt-3">
                                    <label className="block text-xs font-medium text-gray-500 mb-1">Dirección (Requerido DIAN)</label>
                                    <input
                                        type="text"
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                        value={formData.address}
                                        onChange={e => setFormData({ ...formData, address: e.target.value })}
                                        disabled={isSubmitting}
                                        placeholder="Ej: Calle 123 # 45-67"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-3 mt-3">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 mb-1">Ciudad</label>
                                        <input
                                            type="text"
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                            value={formData.city}
                                            onChange={e => setFormData({ ...formData, city: e.target.value })}
                                            disabled={isSubmitting}
                                            placeholder="Bogotá"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 mb-1">Cód. Ciudad</label>
                                        <input
                                            type="text"
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                            value={formData.cityCode}
                                            onChange={e => setFormData({ ...formData, cityCode: e.target.value })}
                                            disabled={isSubmitting}
                                            placeholder="11001"
                                        />
                                    </div>
                                </div>
                            </div>

                            {submitError && (
                                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <p className="text-sm text-red-600">{submitError}</p>
                                </div>
                            )}

                            <div className="flex gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                                    disabled={isSubmitting}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium disabled:opacity-50"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? 'Guardando...' : (editingClient ? 'Actualizar' : 'Crear Cliente')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

        </div>
    );
};

export default ClientListPage;
